import React, { useState, useMemo } from 'react'
import { Calendar, X, ChevronDown } from 'lucide-react'
import useStore from '../store/useStore'

const QUICK_PRESETS = [
  { label: '2026', years: [2026] },
  { label: '2025', years: [2025] },
  { label: '2024', years: [2024] },
  { label: '2025-2026', years: [2025, 2026] },
  { label: 'Tout', years: [] },
]

const PeriodSelector = () => {
  const { filters, updateFilter } = useStore()
  const [showCustom, setShowCustom] = useState(false)

  const activeYears = filters.annee_declaration || []
  const hasDateRange = filters.date_debut || filters.date_fin

  // Determine which preset is active (if any)
  const activePreset = useMemo(() => {
    if (activeYears.length === 0 && !hasDateRange) return 'Tout'
    const match = QUICK_PRESETS.find(p =>
      p.years.length === activeYears.length &&
      p.years.every(y => activeYears.includes(y))
    )
    return match ? match.label : null
  }, [activeYears, hasDateRange])

  const handlePresetClick = (preset) => {
    updateFilter('annee_declaration', preset.years)
    // Clear custom date range when selecting a year preset
    if (preset.years.length > 0) {
      updateFilter('date_debut', null)
      updateFilter('date_fin', null)
    }
  }

  const handleYearToggle = (year) => {
    const current = filters.annee_declaration || []
    const newList = current.includes(year)
      ? current.filter(y => y !== year)
      : [...current, year].sort()
    updateFilter('annee_declaration', newList)
  }

  const handleDateChange = (field, value) => {
    updateFilter(field, value || null)
  }

  const clearPeriod = () => {
    updateFilter('annee_declaration', [])
    updateFilter('date_debut', null)
    updateFilter('date_fin', null)
  }

  const hasPeriod = activeYears.length > 0 || hasDateRange

  return (
    <div className="card-sm" style={{ padding: '0.75rem' }}>
      <div className="flex items-center gap-3 flex-wrap">
        {/* Label */}
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex-shrink-0">
          <Calendar className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Période</span>
        </div>

        {/* Quick presets */}
        <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5 gap-0.5 flex-wrap">
          {QUICK_PRESETS.map(preset => (
            <button
              key={preset.label}
              onClick={() => handlePresetClick(preset)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                activePreset === preset.label
                  ? 'bg-white dark:bg-gray-700 text-primary-700 dark:text-primary-300 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Custom date range toggle */}
        <button
          onClick={() => setShowCustom(!showCustom)}
          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-all duration-200 ${
            hasDateRange
              ? 'border-primary-300 dark:border-primary-600 text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-600'
          }`}
        >
          <Calendar className="w-3 h-3" />
          <span>Dates précises</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${showCustom ? 'rotate-180' : ''}`} />
        </button>

        {/* Clear button */}
        {hasPeriod && (
          <button
            onClick={clearPeriod}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
            title="Effacer la période"
          >
            <X className="w-3 h-3" />
            <span className="hidden sm:inline">Effacer</span>
          </button>
        )}

        {/* Active period indicator */}
        {hasPeriod && (
          <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            {activeYears.length > 0 && (
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {activeYears.length === 1
                  ? activeYears[0]
                  : `${activeYears[0]}–${activeYears[activeYears.length - 1]}`}
              </span>
            )}
            {hasDateRange && (
              <span className="ml-1">
                {filters.date_debut || '…'} → {filters.date_fin || '…'}
              </span>
            )}
          </span>
        )}
      </div>

      {/* Custom date range panel */}
      {showCustom && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                <Calendar className="w-3 h-3" />
                Date de début
              </label>
              <input
                type="date"
                value={filters.date_debut || ''}
                onChange={(e) => handleDateChange('date_debut', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                <Calendar className="w-3 h-3" />
                Date de fin
              </label>
              <input
                type="date"
                value={filters.date_fin || ''}
                onChange={(e) => handleDateChange('date_fin', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              />
            </div>
          </div>
          <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
            La sélection de dates précises affine le filtre par année. Laissez vide pour utiliser uniquement les années.
          </p>
        </div>
      )}
    </div>
  )
}

export default PeriodSelector
